import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@material-ui/styles';
import {
	Button, TextField, Typography, Grid, Select, Card,
	CardHeader, CardContent, Divider, Accordion, AccordionSummary,
	AccordionDetails, TextareaAutosize
} from '@material-ui/core';
import {
	ThemeProvider,
	createMuiTheme,
	MuiThemeProvider,
	withStyles,
} from '@material-ui/core/styles';
import { DataTable, Header, Label } from 'components';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { SetFormState, GetLayer3MessageMo, GetLayer3MessageMt } from '../../../../../../../../../../actions/VoiceActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	},
	tableRow: {
		'&:hover': {
			cursor: 'pointer'
		}
	}
}));

const Layer3DataTables = (props) => {
	const { className, data, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();

	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.voiceReducer.formState);
	const layer3 = useSelector((state) => state.voiceReducer.layer3);
	const layer3MessageMo = useSelector((state) => state.voiceReducer.layer3MessageMo);
	const layer3MessageMt = useSelector((state) => state.voiceReducer.layer3MessageMt);

	const userId = useSelector(state => state.session.user.id);

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;

		let upHidden = true;
		let downHidden = true;

		if (rowData[5] === 'UL') {
			upHidden = true;
			downHidden = false;
		} else if (rowData[5] === 'DL') {
			upHidden = false;
			downHidden = true;
		}

		return (
			<>
				<div hidden={upHidden}>
					<ArrowUpwardIcon color="error" />
				</div>
				<div hidden={downHidden}>
					<ArrowDownwardIcon color="primary" />
				</div>
			</>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'type',
				label: 'Type',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'time',
				label: 'Time',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: val => {
						const cellStyle = {
							whiteSpace: 'nowrap'
						};
						return (
							<div style={cellStyle}>{val}</div>
						);
					}
				}
			},
			{
				name: 'messageType',
				label: 'Message Type',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'protocol',
				label: 'Protocol',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'direction',
				label: 'Direction',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			}
		]
	);

	const _onRowClick = (rowData, rowMeta) => {
		if (rowData[1] === 'MO') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					layer3SelectedType: 'MO',
				}
			});
			dispatch(GetLayer3MessageMo(rowData[0], 'MO', userId));
		} else if (rowData[1] === 'MT') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					layer3SelectedType: 'MT',
				}
			});
			dispatch(GetLayer3MessageMt(rowData[0], 'MT', userId));
		}
	};

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: false,
		expandableRows: false, // Try Adding This
		search: false,
		filter: false,
		viewColumns: false,
		onRowClick: (rowData, rowMeta) => _onRowClick(rowData, rowMeta),
		rowsPerPageOptions: [],
		rowsPerPage: [200]
	});

	useEffect(() => {
		if (formState.values.layer3SelectedType === 'MO') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					layer3MessageMoc: layer3MessageMo,
				}
			});
		} else if (formState.values.layer3SelectedType === 'MT') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					layer3MessageMtc: layer3MessageMt,
				}
			});
		}
	}, [layer3MessageMo, layer3MessageMt]);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={12} xs={12}>
					<Card>
						{/* <CardHeader
							title="Messages"
						/>
						<Divider /> */}
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={6} xs={6}>
									<label>MO</label>
								</Grid>
								<Grid item md={6} xs={6}>
									<label>MT</label>
								</Grid>
							</Grid>
							<Grid container spacing={1}>
								<Grid item md={4} xs={4}>
									<DataTable
										id="voiceMocDataTable"
										title=""
										data={layer3.layer3TableMoc}
										columns={_getTableColumns()}
										// rowsPerPage={10}
										tableBodyMaxHeight="30vh"
										// tableBodyCellMaxWidth="250"
										options={_getTableOptions()}
										onRowClick={_onRowClick}
										className={classes.tableRow}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="layer3MessageMoc"
										name="layer3MessageMoc"
										label="Message"
										InputProps={{
											readOnly: true,
											style: { fontSize: '11px' }
										}}
										variant="outlined"
										// error={_hasError('failureSource')}
										fullWidth
										multiline
										// helperText={
										// _hasError('failureSource') ? formState.errors.failureSource[0] : null
										// }
										// onChange={_handleChange}
										value={formState.values.layer3MessageMoc || ''}
										rowsMax={20}
									/>
								</Grid>
								<Grid item md={4} xs={4}>
									<DataTable
										id="voiceMtcDataTable"
										title=""
										data={layer3.layer3TableMtc}
										columns={_getTableColumns()}
										// rowsPerPage={10}
										tableBodyMaxHeight="30vh"
										// tableBodyCellMaxWidth="250"
										options={_getTableOptions()}
										onRowClick={_onRowClick}
										className={classes.tableRow}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="layer3MessageMtc"
										name="layer3MessageMtc"
										label="Message"
										InputProps={{
											readOnly: true,
											style: { fontSize: '11px' }
										}}
										variant="outlined"
										// error={_hasError('failureSource')}
										fullWidth
										multiline
										// helperText={
										// _hasError('failureSource') ? formState.errors.failureSource[0] : null
										// }
										// onChange={_handleChange}
										value={formState.values.layer3MessageMtc || ''}
										rowsMax={20}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

Layer3DataTables.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default Layer3DataTables;
